import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { queryClient } from "../query";
import {
  createPerson,
  deletePerson,
  fetchChallengeRanking,
  fetchFeatureFlags,
  fetchFieldGames,
  fetchFields,
  fetchGameActions,
  fetchGames,
  fetchPlayersByStat,
  fetchRanking,
  fetchRegister,
  fetchSponsors,
  fetchTeamById,
  fetchTeamPersons,
  fetchTeams,
  fetchTeamsByStat,
  fetchTournamentOptions,
  registerTeam,
  updatePerson,
  updateRegister,
  uploadAttachment,
} from "./airtable";
import { SavePersonInput } from "./types";

export const useFetchSponsors = () => useQuery(["sponsors"], fetchSponsors);

export const useFetchTournamentOptions = (dayPart: "am" | "pm") =>
  useQuery(["tournament-options", dayPart], () =>
    fetchTournamentOptions(dayPart)
  );

export const useFetchRanking = (params: {
  selectedCategory: string | undefined;
  dayPart: "am" | "pm";
}) => useQuery(["ranking", params], () => fetchRanking(params));

export const useFetchGames = (params: {
  selectedCategory: string | undefined;
  dayPart: "am" | "pm";
}) => useQuery(["game", params], () => fetchGames(params));

export const useFetchChallengeRanking = () =>
  useQuery(["challenge-ranking"], fetchChallengeRanking);

export const useFetchFields = () => useQuery(["fields"], fetchFields);

export const useFetchFieldGames = ({
  field,
  dayPart = "am",
}: {
  field?: string;
  dayPart?: "am" | "pm";
}) =>
  useQuery(
    ["field-games", { field, dayPart }],
    () => fetchFieldGames({ field, dayPart }),
    {
      staleTime: 30 * 1000,
    }
  );

export const useFetchFeatureFlags = () =>
  useQuery(["feature-flags"], fetchFeatureFlags);

export const useRegisterTeam = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      if (data.recordId) {
        return updateRegister(data);
      }
      return registerTeam(data);
    },
    async onSuccess(data) {
      enqueueSnackbar("Inscription enregistrée", { variant: "success" });
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de l'enregistrement de votre équipe", {
        variant: "error",
      });
      return error;
    },
  });
};

export const useFetchRegister = (teamId: string | null | undefined) => {
  return useQuery(["register"], () => fetchRegister(teamId), {
    enabled: Boolean(teamId),
  });
};

export const useFetchTeamPersons = (teamId: string | null | undefined) => {
  return useQuery(["team-persons", teamId], () => fetchTeamPersons(teamId), {
    enabled: Boolean(teamId),
  });
};

export const useSavePerson = () => {
  return useMutation({
    mutationFn: async ({
      recordId,
      image,
      autorisation,
      ...data
    }: SavePersonInput) => {
      if (recordId) {
        return updatePerson(recordId, data);
      }
      return createPerson(data);
    },
    async onSuccess(data, variables) {
      const recordId = data?.id;
      if (!recordId) {
        return data;
      }
      if (variables?.image) {
        await uploadAttachment(recordId, "image", variables.image);
      }
      if (variables?.autorisation) {
        await uploadAttachment(
          recordId,
          "autorisation",
          variables.autorisation
        );
      }
      if (variables?.licence) {
        await uploadAttachment(recordId, "licence", variables.licence);
      }
      enqueueSnackbar("Membre ajouté", { variant: "success" });
      await queryClient.invalidateQueries(["team-persons"]);
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de l'ajout du membre", { variant: "error" });
      return error;
    },
  });
};

export const useDeletePerson = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      return deletePerson(id);
    },
    async onSuccess(data) {
      enqueueSnackbar("Membre supprimé", { variant: "success" });
      await queryClient.invalidateQueries(["team-persons"]);
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de la suppresion du membre", {
        variant: "error",
      });
      return error;
    },
  });
};

export const useFetchGameActions = (gameId: string | undefined) => {
  return useQuery(["game-actions", gameId], () => fetchGameActions(gameId), {
    enabled: Boolean(gameId),
  });
};

export const useFetchPlayersByStats = (
  stat: "tries" | "conversions" | "penalties" | "drops" | "points"
) => {
  return useQuery(["players-by-stats", stat], () => fetchPlayersByStat(stat));
};

export const useFetchTeamsByStats = (
  stat: "points_scored" | "points_taken"
) => {
  return useQuery(["teams-by-stats", stat], () => fetchTeamsByStat(stat));
};

export const useFetchTeams = () => {
  return useQuery(["teams"], fetchTeams);
};

export const useFetchTeamById = (id: string | undefined) => {
  return useQuery(["team", id], () => fetchTeamById(id || ""), {
    enabled: Boolean(id),
  });
};
